import { addDays, startOfWeek } from "date-fns";
import * as React from "react";
import { DateNav } from "src/components/DateNav";
import { Page } from "src/components/Page";
import { useUrlParam } from "src/hooks/useUrlParam";
import { humanDate } from "src/utils/humanDate";
import { getLocalDate } from "src/database/utils/localDate";
import styled from "styled-components";
import { useLiveQuery } from "dexie-react-hooks";
import { getWeekPlan } from "src/database/queries/habits";
import { shouldHideTask } from "src/utils/shouldHideTask";

const HEADER_HEIGHT = 40;
const TIME_WIDTH = 60;
const HOUR_HEIGHT = 60;
const MAX_SHADOW_SIZE = 5;

const HEADER_SHADOW_STYLE: React.CSSProperties = {
  top: 0,
  left: TIME_WIDTH,
  right: 0,
  bottom: "auto",
  height: HEADER_HEIGHT,
};

const SIDEBAR_SHADOW_STYLE: React.CSSProperties = {
  top: HEADER_HEIGHT,
  left: 0,
  bottom: 0,
  right: "auto",
  width: TIME_WIDTH,
};

function getTaskPositionY(startMinutes: number) {
  return (startMinutes / 60) * HOUR_HEIGHT;
}

function parseTime(timeStr: string) {
  const [hourStr, minuteStr] = timeStr.split(":");
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  return hour * 60 + minute;
}

function clamp(value: number, min: number, max: number) {
  return value < min ? min : value > max ? max : value;
}

export function Calendar() {
  const calendarRef = React.useRef<HTMLDivElement>(null);
  const columnRef = React.useRef<HTMLDivElement>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const shadowRef = React.useRef<HTMLDivElement>(null);
  const headerShadowRef = React.useRef<HTMLDivElement>(null);
  const sidebarShadowRef = React.useRef<HTMLDivElement>(null);
  const [dateStr, setDate] = useUrlParam("date");
  const date = startOfWeek(new Date(dateStr), { weekStartsOn: 1 });
  const weekdayStr = getLocalDate(date);
  const rawWeekPlan = useLiveQuery(() => getWeekPlan(weekdayStr), [weekdayStr]);

  const weekPlan = React.useMemo(() => {
    if (!rawWeekPlan) return [];
    return rawWeekPlan.map(({ date, tasks }) => ({
      date,
      tasks: tasks.filter((t) => !shouldHideTask(date, t.condition) && t.startTime && t.endTime),
    }));
  }, [rawWeekPlan]);

  const daysOfWeekIndex = Array.from({ length: 7 }, (_, i) => i);

  const hours = Array.from({ length: 24 }).map((_, i) => i);

  React.useEffect(() => {
    const grid = calendarRef.current;
    const column = columnRef.current;
    const row = rowRef.current;

    if (!grid || !column || !row) return;

    const onScroll = () => {
      const scrollTop = clamp(grid.scrollTop, 0, MAX_SHADOW_SIZE);
      const scrollLeft = clamp(grid.scrollLeft, 0, MAX_SHADOW_SIZE);
      const scrollBottom = clamp(
        grid.scrollHeight - grid.clientHeight - grid.scrollTop,
        0,
        MAX_SHADOW_SIZE,
      );
      const scrollRight = clamp(
        grid.scrollWidth - grid.clientWidth - grid.scrollLeft,
        0,
        MAX_SHADOW_SIZE,
      );

      const scrollbarWidth = grid.offsetWidth - grid.clientWidth;
      const scrollbarHeight = grid.offsetHeight - grid.clientHeight;
      shadowRef.current!.style.right = scrollbarWidth + "px";
      shadowRef.current!.style.bottom = scrollbarHeight + "px";
      headerShadowRef.current!.style.right = scrollbarWidth + "px";
      sidebarShadowRef.current!.style.bottom = scrollbarHeight + "px";

      headerShadowRef.current!.style.setProperty("--shadow-left-size", scrollLeft + "px");
      headerShadowRef.current!.style.setProperty("--shadow-right-size", scrollRight + "px");
      headerShadowRef.current!.style.maxWidth = column.scrollWidth + "px";
      sidebarShadowRef.current!.style.setProperty("--shadow-top-size", scrollTop + "px");
      sidebarShadowRef.current!.style.setProperty("--shadow-bottom-size", scrollBottom + "px");
      shadowRef.current!.style.setProperty("--shadow-top-size", scrollTop + "px");
      shadowRef.current!.style.setProperty("--shadow-bottom-size", scrollBottom + "px");
      shadowRef.current!.style.setProperty("--shadow-left-size", scrollLeft + "px");
      shadowRef.current!.style.setProperty("--shadow-right-size", scrollRight + "px");
    };

    window.addEventListener("resize", onScroll);
    grid.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", onScroll);
      grid.removeEventListener("scroll", onScroll);
    };
  }, []);

  React.useEffect(() => {
    if (!weekPlan || !calendarRef.current) return;
    const allTasks = weekPlan
      .flatMap(({ tasks, date }) => tasks.filter((t) => !shouldHideTask(date, t.condition)))
      .filter((t) => t.startTime && t.endTime);
    const sortedTasks = allTasks.sort((a, b) => parseTime(a.startTime!) - parseTime(b.startTime!));
    const earliestTask = sortedTasks.length > 0 ? sortedTasks[0] : null;
    const earliestStart = earliestTask ? parseTime(earliestTask.startTime!) : null;
    if (calendarRef.current && earliestStart) {
      calendarRef.current.scrollTop = getTaskPositionY(earliestStart);
    }
  }, [weekPlan, calendarRef]);

  return (
    <Page scrollable={false} fullWidth>
      <Header>
        <DateNav mode="week" date={date} setDate={(date) => setDate(getLocalDate(date))} />
      </Header>
      <CalendarContainer>
        <Shadow ref={shadowRef} />
        <Shadow ref={headerShadowRef} style={HEADER_SHADOW_STYLE} />
        <Shadow ref={sidebarShadowRef} style={SIDEBAR_SHADOW_STYLE} />
        <CalendarLayout ref={calendarRef}>
          <Corner />
          <CalendarHeader ref={columnRef}>
            {daysOfWeekIndex.map((i) => (
              <DayHeader key={i}>{humanDate(addDays(date, i), { shortYear: true })}</DayHeader>
            ))}
          </CalendarHeader>
          <TimeColumn ref={rowRef}>
            {hours.map((hour) => (
              <TimeUnit key={hour}>{hour}:00</TimeUnit>
            ))}
          </TimeColumn>
          <CalendarView>
            {daysOfWeekIndex.map((i) => {
              const tasks = weekPlan[i]?.tasks ?? [];
              const timeboxes = tasks.filter((t) => t.startTime && t.endTime);
              return (
                <Column key={i}>
                  {timeboxes.map((task, idx) => {
                    if (!task.startTime || !task.endTime) return null;
                    const [startHour, startMinute] = task.startTime.split(":").map(Number);
                    const [endHour, endMinute] = task.endTime.split(":").map(Number);
                    const top = getTaskPositionY(startHour * 60 + startMinute);
                    const height = (endHour + endMinute / 60) * HOUR_HEIGHT - top - 1;
                    return (
                      <Timebox
                        key={idx}
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                        }}
                      >
                        {task.name}
                      </Timebox>
                    );
                  })}
                  {hours.map((hour) => (
                    <TimeWindow key={hour}></TimeWindow>
                  ))}
                </Column>
              );
            })}
          </CalendarView>
        </CalendarLayout>
      </CalendarContainer>
    </Page>
  );
}

const Header = styled.div`
  padding: 0 10px;
`;

const CalendarContainer = styled.div`
  position: relative;
  overflow: hidden;
`;

const CalendarLayout = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
  grid-template-rows: 40px 1fr;
  overflow: auto;
  max-width: 100%;
  height: 100%;
  scrollbar-width: thin;
`;

const Corner = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: ${TIME_WIDTH}px;
  height: ${HEADER_HEIGHT}px;
  background: var(--box-color);
  z-index: 4;
`;

const CalendarView = styled.div`
  display: flex;
  min-height: 0;
  position: relative;
  overscroll-behavior: contain;
  &:after {
    content: "";
    position: sticky;
    left: 0;
    top: 0;
  }
`;

const Shadow = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  top: ${HEADER_HEIGHT}px;
  left: ${TIME_WIDTH}px;
  pointer-events: none;
  z-index: 5;
  --shadow-left-size: 0px;
  --shadow-top-size: 0px;
  --shadow-right-size: 0px;
  --shadow-bottom-size: 0px;
  background:
    linear-gradient(to bottom, rgba(0, 0, 0, 0.15), transparent) top,
    linear-gradient(to top, rgba(0, 0, 0, 0.15), transparent) bottom,
    linear-gradient(to right, rgba(0, 0, 0, 0.15), transparent) left,
    linear-gradient(to left, rgba(0, 0, 0, 0.15), transparent) right;
  background-repeat: no-repeat;
  background-size:
    100% var(--shadow-top-size),
    100% var(--shadow-bottom-size),
    var(--shadow-left-size) 100%,
    var(--shadow-right-size) 100%;
`;

const Column = styled.div`
  position: relative;
  flex-grow: 1;
  flex-basis: 0;
  flex-shrink: 0;
  min-width: 150px;
`;

const CalendarHeader = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 3;
`;

const DayHeader = styled.div`
  height: ${HEADER_HEIGHT}px;
  flex-grow: 1;
  flex-basis: 0;
  min-width: 150px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 300;
  color: var(--text-secondary-color);
  background: var(--box-color);
`;

const TimeColumn = styled.div`
  position: sticky;
  left: 0;
  z-index: 2;
  width: ${TIME_WIDTH}px;
`;

const TimeWindow = styled.div`
  height: ${HOUR_HEIGHT}px;
  border-bottom: 1px solid var(--box-border-color);
  border-right: 1px solid var(--box-border-color);
  position: relative;
  &:last-child {
    border-bottom: none;
  }
  ${Column}:last-child & {
    border-right: none;
  }
  &:after {
    position: absolute;
    content: "";
    top: ${HOUR_HEIGHT / 2}px;
    left: 0;
    right: 0;
    border-top: 1px solid var(--box-border-color);
    opacity: 0.25;
  }
`;

const TimeUnit = styled.div`
  position: relative;
  height: ${HOUR_HEIGHT}px;
  padding-top: 5px;
  padding-right: 5px;
  text-align: right;
  border-bottom: 1px solid var(--box-border-color);
  font-size: 12px;
  color: var(--text-secondary-color);
  background: var(--box-color);
  &:last-child {
    border-bottom: none;
  }
  &:after {
    position: absolute;
    content: "";
    top: ${HOUR_HEIGHT / 2}px;
    left: 0;
    right: 0;
    border-top: 1px solid var(--box-border-color);
    opacity: 0.25;
  }
`;

const Timebox = styled.div`
  position: absolute;
  z-index: 1;
  left: 5px;
  right: 6px;
  background-color: var(--box-color);
  border-radius: 10px;
  padding: 5px 7px;
  font-size: 12px;
  color: var(--text-primary-color);
  cursor: move;
  cursor: grab;
  user-select: none;
  overflow: auto;
  border-bottom: 2px solid var(--box-color-hover);
`;
